import { computed, inject, Injectable, signal } from '@angular/core';

import { CtaCooldownService } from './cta-cooldown.service';
import { FALLBACK_CTA, FULL_PAGE_CTAS, PAGE_CTA_CONFIG } from './cta.constants';
import { CtaDefinition, CtaSurface } from './cta.models';

@Injectable()
export class CtaStore {
  readonly #cooldown = inject(CtaCooldownService);

  readonly #surface = signal<CtaSurface>(CtaSurface.Page);
  readonly #currentPath = signal<string | null>(null);

  /** CTAs configured for the current surface, before cooldown filtering. */
  readonly #surfaceCtas = computed<CtaDefinition[]>(() => {
    if (this.#surface() === CtaSurface.FullPage) {
      return FULL_PAGE_CTAS;
    }

    const path = this.#currentPath();
    if (path === null) {
      return [];
    }

    const supportedPage = matchSupportedPage(path);
    if (supportedPage === null) {
      return [];
    }

    return [...PAGE_CTA_CONFIG[supportedPage]].sort(
      (first, second) => first.order - second.order,
    );
  });

  readonly #candidates = computed<CtaDefinition[]>(() => {
    const inCooldown = this.#cooldown.activeCooldownIds();

    return this.#surfaceCtas().filter(cta => !inCooldown.has(cta.id));
  });

  /**
   * CTAs to render for the current surface. Only the page surface falls back to
   * the single fallback CTA when the context has none configured (e.g.
   * `/personal-info`) or when every context CTA is in cooldown. The popup and
   * full-page chat never show the fallback — they render nothing instead.
   */
  readonly visibleCtas = computed<CtaDefinition[]>(() => {
    const candidates = this.#candidates();

    if (candidates.length) {
      return candidates;
    }

    return this.#surface() === CtaSurface.Page ? [FALLBACK_CTA] : [];
  });

  /** Whether the current surface/page is configured to show CTAs at all. */
  readonly isSupportedContext = computed<boolean>(() => {
    if (this.#surface() === CtaSurface.FullPage) {
      return true;
    }

    const path = this.#currentPath();

    return path !== null && matchSupportedPage(path) !== null;
  });

  setSurface(surface: CtaSurface): void {
    this.#surface.set(surface);
  }

  setCurrentPath(path: string | null): void {
    this.#currentPath.set(path);
  }

  /** Load the active user's persisted cooldowns. Call on surface mount/navigation. */
  syncCooldowns(): void {
    this.#cooldown.syncForCurrentUser();
  }
}

function normalizeCtaPath(path: string): string {
  const withoutQueryOrHash = path.split('?')[0].split('#')[0];

  return withoutQueryOrHash.length > 1 && withoutQueryOrHash.endsWith('/')
    ? withoutQueryOrHash.slice(0, -1)
    : withoutQueryOrHash;
}

/**
 * Resolves the current route to a supported page key. A page matches when the
 * route equals the configured path or is nested under it (e.g.
 * `/education/my-courses/all` matches `/education/my-courses`). When several
 * configured pages match, the most specific (longest) one wins.
 */
function matchSupportedPage(path: string): string | null {
  const normalized = normalizeCtaPath(path);

  let bestMatch: string | null = null;
  for (const configuredPath of Object.keys(PAGE_CTA_CONFIG)) {
    const isMatch = normalized === configuredPath || normalized.startsWith(`${configuredPath}/`);
    if (isMatch && (bestMatch === null || configuredPath.length > bestMatch.length)) {
      bestMatch = configuredPath;
    }
  }

  return bestMatch;
}
